import { Button, InlineAlert } from '..'
import CanonicalModal from '../CanonicalModal/CanonicalModal'

type ResendInviteModalProps = {
	open: boolean
	onCancel: () => void
	onConfirm: () => void
	fullName?: string
	email?: string
	signupUrl?: string
	isExpired?: boolean
	// #873: disables both buttons while the resend/renew call is in flight - prevents a
	// double-submit (and a second invalidate-and-recreate) racing the first.
	loading?: boolean
	// #873: a failed request may follow a server-side invitation rotation, so show an uncertain
	// outcome without exposing the potentially stale link or offering an immediate retry.
	error?: string | null
	// #873: once the resend/renew actually succeeds, the parent passes the *new* invitation's
	// outcome here instead of closing the modal immediately - so the admin is shown the current,
	// usable link (never the just-invalidated one) and is told plainly if the invitation email's
	// delivery couldn't be confirmed (never claimed as "sent" nor "definitely unsent"). `delivery`
	// is omitted (not `'sent'`) for callers whose backend route doesn't report a delivery outcome
	// (e.g. the supply-chain-invitation resend, out of #873's scope) - no delivery note is shown
	// rather than fabricating one.
	result?: { signupUrl: string, delivery?: 'sent' | 'unconfirmed' } | null
}

/**
 * Shared "Resend invite"/"Renew invite" confirmation modal (Sub-task I, docs/todo/
 * user-org-membership-i-qa-actions.md item A.3) - used by both AdminTrainingSessionUsers.tsx and
 * AdminUsers.tsx so both an admin invitation and a supply-chain invitation resend look identical,
 * regardless of which backend endpoint the caller's `onConfirm` actually hits. Shows the
 * invitation's *current* signup link (not yet regenerated) - confirming invalidates it and emails
 * a fresh one, mirroring the existing resendInvite.ts/resendSupplyChainInvite.ts lib behaviour.
 */
function ResendInviteModal({
	open,
	onCancel,
	onConfirm,
	fullName,
	email,
	signupUrl,
	isExpired,
	loading,
	error,
	result,
}: ResendInviteModalProps) {
	const title = isExpired ? 'Renew invite' : 'Resend invite'
	const subtitle = [fullName, email].filter(Boolean).join(' ') || undefined

	if (error) {
		return (
			<CanonicalModal
				open={open}
				onClose={onCancel}
				title={title}
				subtitle={subtitle}
				actions={<Button type='Primary' text='Close' onClick={onCancel} />}
			>
				<InlineAlert type='warning' className='mt-8'>
					{error}
				</InlineAlert>
			</CanonicalModal>
		)
	}

	// #873: once `result` is set the rotation has already happened server-side - show the new
	// link (never the superseded one above) and a single explicit close action, instead of the
	// confirm/cancel pair that no longer applies.
	if (result) {
		return (
			<CanonicalModal
				open={open}
				onClose={onCancel}
				title={title}
				subtitle={subtitle}
				actions={<Button type='Primary' text='Close' onClick={onCancel} />}
			>
				<p className='text-white mt-8'>
					{result.delivery === 'unconfirmed'
						? 'The invitation was updated, but we could not confirm its email was sent.'
						: isExpired
						? 'A new invitation has been sent.'
						: 'This invitation has been resent.'}
				</p>
				{result.delivery === 'unconfirmed' && (
					<InlineAlert type='warning' className='mt-4'>
						Check the inbox. If the email does not arrive, use Resend invite on the user list.
					</InlineAlert>
				)}
				<div className='mt-8'>
					<h4 className='text-sm text-charcoal-100 my-1'>Unique signup link</h4>
					<div className='bg-charcoal-700 rounded p-4 text-charcoal-100 text-sm break-all font-mono'>
						{result.signupUrl}
					</div>
				</div>
			</CanonicalModal>
		)
	}

	return (
		<CanonicalModal
			open={open}
			onClose={onCancel}
			title={title}
			subtitle={subtitle}
			actions={
				<>
					<Button
						type='Primary'
						text={title}
						onClick={onConfirm}
						disabled={!!loading}
						additionalClass='mr-5'
					/>
					<Button type='Outlined' text='Cancel' onClick={onCancel} disabled={!!loading} />
				</>
			}
		>
			<p className='text-white mt-8'>
				{isExpired
					? 'This invitation has expired. Sending a new one will invalidate the expired link below.'
					: 'Are you sure you want to resend this invitation? This will invalidate the link below and generate a new one.'}
			</p>
			{signupUrl && (
				<div className='mt-8'>
					<h4 className='text-sm text-charcoal-100 my-1'>Unique signup link</h4>
					<div className='bg-charcoal-700 rounded p-4 text-charcoal-100 text-sm break-all font-mono'>
						{signupUrl}
					</div>
				</div>
			)}
		</CanonicalModal>
	)
}

export default ResendInviteModal
